import React from "react";
import { Button } from "@/components/ui/button";
import { CalendarDays, PauseCircle, Pencil, Trash2, RefreshCw, Plus } from "lucide-react";
import { formatDateEU } from "@/utils/dateFormat";

const PAYMENT_OPTIONS = [
  { value: "CARD", label: "Bancontact / myPOS" },
  { value: "TRANSFER", label: "Virement / Facture" },
  { value: "CASH", label: "Cash / Numéraire" },
  { value: "DOMICILIATION", label: "Domiciliation" },
  { value: "OTHER", label: "FloaPay / Autre" },
];
const paymentLabel = (val) => PAYMENT_OPTIONS.find(p => p.value === val)?.label || val;

const SuspensionsList = ({ suspensions, onDeleteSuspension }) => {
  if (!suspensions?.length) return null;
  return (
    <div className="mt-3 pt-3 border-t border-[#edf0f4]">
      <p className="text-xs font-semibold text-[#577083] mb-2">Suspensions ({suspensions.length})</p>
      {suspensions.map((susp) => (
        <div key={susp.id} className="flex items-center justify-between py-1 text-xs">
          <div className="flex items-center gap-2">
            <PauseCircle size={12} className="text-blue-500" />
            <span>{formatDateEU(susp.suspension_start)} → {formatDateEU(susp.suspension_end)}</span>
            <span className="crm-badge bg-blue-50 text-blue-600">{susp.days_suspended}j</span>
            <span className="text-[#8fa4b5]">{susp.reason}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onDeleteSuspension(susp.id)}><Trash2 size={12} className="text-red-400" /></Button>
        </div>
      ))}
    </div>
  );
};

export const SubscriptionCard = ({ sub, suspensions, onEdit, onDelete, onToggleStatus, onRenew, onNotRenewed, onAddSuspension, onDeleteSuspension }) => (
  <div className="crm-sub-card">
    <div className="flex items-start justify-between">
      <div className="flex flex-wrap items-center gap-1">
        <span className="font-semibold text-[#162d3d]">{sub.plan_name}</span>
        <span className="crm-badge bg-[#edf0f4] text-[#32536a]">{sub.plan_category}</span>
        {sub.payment_type === "DOMICILIATION" && <span className="crm-badge bg-yellow-100 text-yellow-700">Domiciliation</span>}
        <button
          onClick={() => onToggleStatus(sub)}
          className={`crm-badge cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${sub.is_active ? 'bg-green-100 text-green-700 hover:ring-green-300' : 'bg-red-100 text-red-700 hover:ring-red-300'}`}
          title="Cliquez pour changer le statut"
          data-testid={`toggle-status-${sub.id}`}
        >
          {sub.is_active ? '✓ Actif' : '✗ Inactif'}
        </button>
        {sub.subscription_status && sub.subscription_status !== "AUTO" && <span className="crm-badge bg-purple-100 text-purple-700 text-xs">Manuel</span>}
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => onEdit(sub)} data-testid={`edit-sub-${sub.id}`}><Pencil size={14} className="text-[#3899ec]" /></Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(sub.id)}><Trash2 size={14} className="text-red-400" /></Button>
      </div>
    </div>
    <div className="crm-sub-details">
      <div><CalendarDays size={14} /> <span>Début: {formatDateEU(sub.start_date)}</span></div>
      <div><CalendarDays size={14} /> <span>Échéance: {formatDateEU(sub.end_date_effective || sub.end_date)} {sub.end_date_indicative ? "(indicative)" : ""}</span></div>
      <div><span className="font-medium">{sub.price_eur}€</span> — {paymentLabel(sub.payment_type)}</div>
      {sub.total_suspension_days > 0 && <div className="text-blue-600">Suspension: +{sub.total_suspension_days}j</div>}
      {sub.days_remaining != null && <div className={sub.days_remaining < 0 ? "text-red-500 font-medium" : sub.days_remaining <= 15 ? "text-red-500" : sub.days_remaining <= 30 ? "text-orange-500" : "text-green-600"}>{sub.days_remaining < 0 ? `Expiré (${Math.abs(sub.days_remaining)}j)` : `${sub.days_remaining} jours restants`}</div>}
    </div>
    <div className="flex gap-2 mt-2 flex-wrap">
      <Button size="sm" variant="outline" onClick={() => onRenew(sub)}
        className={`${sub.renewal_decision === "RENEWED" ? "bg-green-50 border-green-300" : "border-green-200 hover:bg-green-50"}`}
        data-testid={`renew-btn-${sub.id}`}>
        <RefreshCw size={13} className="mr-1" /> Renouveler
      </Button>
      <Button size="sm" variant="outline" onClick={() => onNotRenewed(sub.id)}
        className={sub.renewal_decision === "NOT_RENEWED" ? "bg-red-50 border-red-300" : ""}>
        Non reconduit
      </Button>
      <Button size="sm" variant="outline" onClick={() => onAddSuspension(sub.id)}>
        <PauseCircle size={14} className="mr-1" /> Suspension
      </Button>
    </div>
    {sub.renewal_decision === "RENEWED" && (
      <div className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1"><RefreshCw size={12} /> Renouvellement validé</div>
    )}
    <SuspensionsList suspensions={suspensions} onDeleteSuspension={onDeleteSuspension} />
  </div>
);

export const SubscriptionList = ({ subs, suspensions, onAdd, ...handlers }) => (
  <div className="crm-section">
    <div className="flex items-center justify-between mb-4">
      <h2 className="crm-section-title">Abonnements</h2>
      <Button size="sm" onClick={onAdd} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">
        <Plus size={14} className="mr-1" /> Ajouter
      </Button>
    </div>
    {subs.length === 0 ? <p className="text-sm text-[#577083]">Aucun abonnement</p> : (
      <div className="flex flex-col gap-3">
        {subs.map((s) => (
          <SubscriptionCard key={s.id || s._id} sub={s} suspensions={suspensions[s.id]} {...handlers} />
        ))}
      </div>
    )}
  </div>
);

export { PAYMENT_OPTIONS, paymentLabel };
